// TODO: vitest component test (pages are covered by Playwright e2e)
import { useEffect } from 'react'
import { useParams, useNavigate } from 'react-router'
import { useTranslation } from 'react-i18next'
import { useForm } from 'react-hook-form'
import { PageHeader, Breadcrumbs } from '@/components/layout'
import { Button, Card, Checkbox, FormActions, Input, Textarea } from '@/components/ui'
import { useVendorDetail, useCreateVendor, useUpdateVendor } from '@/hooks/useCmms'

interface VendorFormData {
  name: string
  contact_name: string
  phone: string
  email: string
  address: string
  specialties: string
  notes: string
  active: boolean
}

export function VendorFormPage() {
  const { t } = useTranslation('cmms')
  const { t: tc } = useTranslation('common')
  const { id } = useParams<{ id: string }>()
  const isEditing = !!id
  const vendorId = Number(id)
  const navigate = useNavigate()

  const { data: existingVendor, isLoading: isLoadingVendor } = useVendorDetail(
    isEditing ? vendorId : 0
  )
  const createVendor = useCreateVendor()
  const updateVendor = useUpdateVendor()

  const {
    register,
    handleSubmit,
    reset,
    formState: { errors },
  } = useForm<VendorFormData>({
    defaultValues: {
      name: '',
      contact_name: '',
      phone: '',
      email: '',
      address: '',
      specialties: '',
      notes: '',
      active: true,
    },
  })

  useEffect(() => {
    if (isEditing && existingVendor) {
      reset({
        name: existingVendor.name,
        contact_name: existingVendor.contact_name || '',
        phone: existingVendor.phone || '',
        email: existingVendor.email || '',
        address: existingVendor.address || '',
        specialties: existingVendor.specialties ? existingVendor.specialties.join(', ') : '',
        notes: existingVendor.notes || '',
        active: existingVendor.active,
      })
    }
  }, [isEditing, existingVendor, reset])

  const onSubmit = async (formData: VendorFormData) => {
    const specialtiesArray = formData.specialties
      ? formData.specialties.split(',').map((s) => s.trim()).filter(Boolean)
      : []

    const payload = {
      name: formData.name,
      contact_name: formData.contact_name || undefined,
      phone: formData.phone || undefined,
      email: formData.email || undefined,
      address: formData.address || undefined,
      specialties: specialtiesArray,
      notes: formData.notes || undefined,
      active: formData.active,
    }

    if (isEditing) {
      await updateVendor.mutateAsync({ id: vendorId, ...payload })
      navigate(`/vendors/${vendorId}`)
    } else {
      const result = await createVendor.mutateAsync(payload)
      navigate(`/vendors/${result.id}`)
    }
  }

  if (isEditing && isLoadingVendor) {
    return (
      <div className="space-y-6">
        <Card><div className="h-64 animate-pulse bg-base-200 rounded" /></Card>
      </div>
    )
  }

  return (
    <div className="space-y-6">
      <Breadcrumbs
        items={[
          { label: t('vendors.title'), href: '/vendors' },
          { label: isEditing ? t('vendors.edit') : t('vendors.new') },
        ]}
      />

      <PageHeader
        title={isEditing ? t('form.title_edit_vendor') : t('form.title_new_vendor')}
        description={isEditing ? t('form.description_edit_vendor') : t('form.description_new_vendor')}
      />

      <Card>
        <form onSubmit={handleSubmit(onSubmit)} className="space-y-6">
          <div className="grid gap-6 md:grid-cols-2">
            <Input
              label={t('labels.name')}
              error={errors.name?.message}
              {...register('name', { required: tc('errors.required') })}
            />
            <Input
              label={t('labels.contact_name')}
              {...register('contact_name')}
            />
            <Input
              label={t('labels.phone')}
              type="tel"
              {...register('phone')}
            />
            <Input
              label={t('labels.email')}
              type="email"
              {...register('email')}
            />
          </div>

          <Textarea
            label={t('labels.address')}
            rows={2}
            {...register('address')}
          />
          <Input
            label={t('labels.specialties')}
            placeholder={t('form.specialties_placeholder')}
            {...register('specialties')}
          />
          <Textarea
            label={t('labels.notes')}
            rows={2}
            {...register('notes')}
          />

          <Checkbox size="sm" label={t('labels.active')} {...register('active')} />

          <FormActions>
            <Button type="button" variant="ghost" onClick={() => navigate(-1)}>
              {tc('actions.cancel')}
            </Button>
            <Button
              type="submit"
              loading={isEditing ? updateVendor.isPending : createVendor.isPending}
            >
              {isEditing ? tc('actions.save_changes') : tc('actions.create')}
            </Button>
          </FormActions>
        </form>
      </Card>
    </div>
  )
}
